import React from 'react';
import { Plus, Minus } from 'lucide-react';
import ProductAvatar from '../../../components/common/ProductAvatar';
import { useCart } from '../../customer/context/CartContext';

const PosProductCard = ({ product }) => {
    const { addToCart, removeFromCart, getCartQuantity, setQuantity } = useCart();
    const quantity = getCartQuantity(product.id);

    const handleQuantityChange = (e) => {
        const val = e.target.value;
        if (val === '') {
            setQuantity(product, 0);
            return;
        }
        const parsed = parseInt(val, 10);
        if (!isNaN(parsed) && parsed >= 0) {
            setQuantity(product, parsed);
        }
    };

    return (
        <div className={`rounded-xl shadow-sm p-3 md:p-4 transition-all relative border ${
            quantity > 0 
                ? 'bg-blue-50/40 border-blue-400 ring-2 ring-blue-400/10 shadow-md' 
                : 'bg-white border-gray-100 hover:shadow-md'
        }`}>
            <div className="flex items-start gap-3">
                {/* Product Image */}
                <ProductAvatar
                    name={product.name}
                    image={product.image}
                    size="lg"
                    className="flex-shrink-0"
                />

                {/* Product Details */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                        <h3 className="font-bold text-gray-800 text-sm md:text-base">{product.name}</h3>
                    </div>

                    {/* Controls Row */}
                    <div className="flex items-center justify-between gap-2 mt-2">
                        {/* Unit Label */}
                        <div className="text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-lg uppercase">
                            {product.unit || product.defaultUnit}
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-1 bg-gray-50 rounded-lg px-1.5 py-1.5 border border-gray-200">
                            <button
                                onClick={() => removeFromCart(product.id)}
                                disabled={quantity === 0}
                                className={`w-6 h-6 flex items-center justify-center rounded ${quantity === 0
                                    ? 'text-gray-300 cursor-not-allowed'
                                    : 'text-blue-600 hover:bg-gray-100'
                                    } `}
                            >
                                <Minus className="w-3.5 h-3.5" />
                            </button>
                            <input
                                type="number"
                                min="0"
                                value={quantity || ''}
                                onChange={handleQuantityChange}
                                placeholder="0"
                                className="w-10 text-center font-bold text-gray-800 text-sm bg-transparent border-none outline-none focus:ring-0 p-0 m-0 hide-number-spinners"
                            />
                            <button
                                onClick={() => addToCart(product)}
                                className="w-6 h-6 flex items-center justify-center rounded text-blue-600 hover:bg-gray-100"
                            >
                                <Plus className="w-3.5 h-3.5" />
                            </button>
                        </div>

                        {/* Price */}
                        <div className="text-right">
                            {product.hasDiscount && (
                                <div className="text-xs text-gray-400 line-through">
                                    AED {product.originalPrice?.toFixed(2)}
                                </div>
                            )}
                            <div className="flex items-center gap-1 justify-end">
                                <span className="font-bold text-gray-800 text-sm">AED {product.price?.toFixed(2)}</span>
                                {!product.priceIncludesVat && (
                                    <span className="text-[9px] font-bold text-orange-500 bg-orange-50 px-1 py-0.5 rounded" title={`Price excludes ${product.vatRate}% VAT`}>
                                        +{product.vatRate}% VAT
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PosProductCard;
